import { ISelect } from "src/components/Select";
import { errorNotification } from "./alerts";
import { parse } from "papaparse";

export const debounce = <F extends (...args: any[]) => any>(
  func: F,
  waitFor: number
) => {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<F>): Promise<ReturnType<F>> =>
    new Promise((resolve) => {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(() => resolve(func(...args)), waitFor);
    });
};

export const getFileNameCsv = (name: string, dateRange: [Date, Date]) => {
  const formattedStartDate = dateRange[0]
    .toLocaleDateString()
    .replace(/[/:]/g, "_");
  const formattedEndDate = dateRange[1]
    .toLocaleDateString()
    .replace(/[/:]/g, "_");
  return `${name}_${formattedStartDate}__${formattedEndDate}.csv`;
};

export const convertToCSV = <T extends Record<keyof T, string | number>>(
  data: T[]
): string => {
  const header = Object.keys(data[0]).join(",") + "\n";
  const body = data.map((row) => Object.values(row).join(",")).join("\n");
  return header + body;
};

export const downloadCSV = (csvData: string | undefined, filename: string) => {
  if (!csvData)
    return errorNotification({
      message: "No Data found",
    });

  const csvContent = `\ufeff${csvData}`;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export const handleCopyClick = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error("Unable to copy text: ", error);
  }
};

export const checkIsWeb = () => {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator?.userAgent
    )
  ) {
    return false;
  }
  return true;
};

var hexlist = "0123456789abcdef";
var b64list =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

// GUID string with four dashes is always MSB first,
// but base-64 GUID's vary by target-system endian-ness.
// Little-endian systems are far more common.  Set le==true
// when target system is little-endian (e.g., x86 machine).
//
export const guid_to_base64 = (g: string) => {
  var s = g.replace(/[^0-9a-f]/gi, "").toLowerCase();
  if (s.length != 32) return "";

  s += "0";

  var a, p, q;
  var r = "";
  var i = 0;
  while (i < 33) {
    a =
      (hexlist.indexOf(s.charAt(i++)) << 8) |
      (hexlist.indexOf(s.charAt(i++)) << 4) |
      hexlist.indexOf(s.charAt(i++));

    p = a >> 6;
    q = a & 63;

    r += b64list.charAt(p) + b64list.charAt(q);
  }
  r += "==";

  return r;
}; // guid_to_base64()

export const base64_to_guid = (str: string) => {
  const raw = atob(str);
  let result = "";
  for (let i = 0; i < raw.length; i++) {
    const hex = raw.charCodeAt(i).toString(16);
    result += hex.length === 2 ? hex : "0" + hex;
  }

  let ans = result.toUpperCase();

  return `${ans.substring(0, 8)}-${ans.substring(8, 12)}-${ans.substring(
    12,
    16
  )}-${ans.substring(16, 20)}-${ans.substring(20)}`;
};

export function generateDurationArray(
  minDuration: number,
  maxDuration: number,
  intervalDuration: number
) {
  const durationArray: ISelect[] = [];
  for (let i = minDuration; i <= maxDuration; i += intervalDuration) {
    durationArray.push({ value: String(i), label: String(i) });
  }
  return durationArray;
}

export type IJsonArr = Record<string, any>[];

export const parseCsvToJson = (base64String: string) => {
  // Remove the prefix before decoding
  const dataString = base64String.replace(/^data:text\/csv;base64,/, "");
  const decodedString = atob(dataString);
  // Remove any non-printable characters at the beginning
  const csvCleaned = decodedString.replace(/^[^\x20-\x7E]+/, "");
  let data: IJsonArr = [];
  parse(csvCleaned, {
    header: true,
    complete: function (results: { data: IJsonArr }) {
      data = results.data;
    },
    error: function (error: any) {
      errorNotification({
        message: `Parsing error: ${error.message}`,
      });
    },
  });
  return data;
};

export function generateInitials(fullName: string): string {
  const words = fullName.split(" ");
  // Extract the first letter of each word and convert to uppercase
  const initials = words.map((word) => word.charAt(0).toUpperCase());
  // Combine the initials into a single string
  return initials.join("");
}
